import React, { useState, useEffect, useContext } from 'react';
import './Listitem.css';
import { getImageSrc } from '../../utils/imageUtils'; 
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import { UrlContext } from '../../Context/UrlContext';

// Common fetch configuration
const fetchConfig = {
    credentials: 'include',
    headers: {
        'Content-Type': 'application/json',
    },
};

// Standard sizes for checkboxes
const STANDARD_SIZES = ["S", "M", "L", "XL", "XXL"];

function Listitem() {

    const { apiBaseUrl } = useContext(UrlContext);

    // API base URL
    const API_BASE_URL = apiBaseUrl;

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Use null for no product editing, and a copy of the product data for editing
    const [editingProduct, setEditingProduct] = useState(null); 
    const [categories, setCategories] = useState([]);
    
    // State for manual sizes in the edit modal
    const [manualSizes, setManualSizes] = useState(['']); 
    
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'warning',
        onConfirm: () => {},
        confirmText: 'OK', 
    });

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, []); 

    /**
     * Effect to initialize the editing form state when a product is selected for edit.
     */
    useEffect(() => {
        if (editingProduct) {
            // Filter out standard sizes to populate manualSizes
            const productSizes = editingProduct.sizes || [];
            const customSizes = productSizes.filter(size => !STANDARD_SIZES.includes(size));
            
            // Initialize with custom sizes or one empty input if none exist
            setManualSizes(customSizes.length > 0 ? customSizes : ['']); 
        }
    }, [editingProduct]);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${API_BASE_URL}/products`, fetchConfig);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            // Maps product data, ensuring 'sizes' is an array and setting correct keys for React state
            setProducts(data.map(product => ({
                ...product,
                sizes: Array.isArray(product.sizes) ? product.sizes : [],
                // Ensure number fields are numeric
                price: Number(product.price),
                deliveryPrice: Number(product.deliveryPrice),
                stockCount: Number(product.stockCount),
                // Rename keys for easier form access if desired, but here we keep the backend keys:
                // name -> product_name, category -> category_name, product_type -> product_type
            })));
        } catch (error) {
            console.error('Error fetching products:', error);
            setError('Failed to load products: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/categories`, fetchConfig);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setCategories(data);
        } catch (error) {
            console.error('Error fetching categories:', error);
            setError('Failed to load categories: ' + error.message);
        }
    };

    const showConfirmation = (config) => {
        setConfirmModal({
            isOpen: true,
            confirmText: config.confirmText || 'OK', 
            ...config
        });
    };

    const closeConfirmation = () => {
        setConfirmModal(prev => ({
            ...prev,
            isOpen: false,
        }));
    };

    const handleDelete = async (productId) => {
        showConfirmation({
            title: 'Delete Product',
            message: 'Are you sure you want to delete this product? This action cannot be undone.',
            type: 'danger',
            confirmText: 'Delete',
            onConfirm: async () => {
                try {
                    const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
                        ...fetchConfig,
                        method: 'DELETE',
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
                    }

                    await fetchProducts(); 
                    showConfirmation({
                        title: 'Success',
                        message: 'Product deleted successfully',
                        type: 'info',
                        confirmText: 'OK',
                        onConfirm: closeConfirmation
                    });
                } catch (error) {
                    console.error('Error deleting product:', error);
                    showConfirmation({
                        title: 'Error',
                        message: 'Failed to delete product: ' + error.message,
                        type: 'danger',
                        confirmText: 'OK',
                        onConfirm: closeConfirmation
                    });
                }
            }
        });
    };

    const handleUpdate = async (productId, updatedData) => {
        if (!productId) {
             showConfirmation({
                 title: 'Error',
                 message: 'Invalid product ID for update.',
                 type: 'danger',
                 confirmText: 'OK',
                 onConfirm: closeConfirmation
             });
             return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
                ...fetchConfig,
                method: 'PUT',
                body: JSON.stringify(updatedData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }

            // Successfully updated
            await fetchProducts();
            setEditingProduct(null); // Close the modal
            setManualSizes(['']); // Clear manual sizes state 
            showConfirmation({
                title: 'Success',
                message: 'Product updated successfully',
                type: 'info',
                confirmText: 'OK',
                onConfirm: closeConfirmation
            });
        } catch (error) {
            console.error('Error updating product:', error);
            showConfirmation({
                title: 'Error',
                message: 'Failed to update product: ' + error.message,
                type: 'danger',
                confirmText: 'OK',
                onConfirm: closeConfirmation
            });
        }
    };

    /**
     * UNIFIED HANDLER: Maps form names to product state keys and handles type conversion.
     * Maps:
     * productName -> name
     * categoryName -> category
     * productType -> product_type
     * others (description, price, etc.) -> their own name
     */
    const handleEditChange = (e) => {
        const { name, value, type } = e.target;
        
        setEditingProduct(prev => {
            if (!prev) return null;
            
            let newValue = value;
            
            // Map form field names to product object keys
            const keyMap = {
                productName: 'name',
                categoryName: 'category',
                productType: 'product_type',
                description: 'description',
                price: 'price',
                deliveryPrice: 'deliveryPrice',
                stockCount: 'stockCount',
                // 'image' field is not updated by this handler
            };
            const key = keyMap[name] || name;

            // Handle number conversion
            if (type === 'number') {
                // Parse float for price/deliveryPrice, integer for stockCount
                if (name === 'price' || name === 'deliveryPrice') {
                    // Use Number() for better handling of empty string or non-numeric input from user
                    newValue = Number(value); 
                } else if (name === 'stockCount') {
                    // Use Number() for better handling of empty string or non-numeric input from user
                    newValue = Number(value);
                }
            }
            
            return {
                ...prev,
                [key]: newValue
            };
        });
    };
    
    // Handler for standard size checkboxes (updates editingProduct.sizes array)
    const handleStandardSizeToggle = (e) => {
        const size = e.target.value;
        const isChecked = e.target.checked;

        setEditingProduct(prev => {
            if (!prev) return null;
            
            const prevSizes = Array.isArray(prev.sizes) ? prev.sizes : [];
            let newSizes;
            
            if (isChecked) {
                // Add size only if it's not already present
                newSizes = [...new Set([...prevSizes, size])]; 
            } else {
                // Remove size
                newSizes = prevSizes.filter(s => s !== size);
            }

            return {
                ...prev,
                sizes: newSizes
            };
        });
    };


    // Handlers for manual size inputs
    const handleManualSizeChange = (i, val) => {
        const newSizes = [...manualSizes];
        newSizes[i] = val;
        setManualSizes(newSizes);
    };

    const addManualSize = () => {
        // Prevent adding a new field if the last one is empty
        if (manualSizes[manualSizes.length - 1].trim() !== '') {
            setManualSizes([...manualSizes, '']);
        }
    }
    const removeManualSize = i => setManualSizes(manualSizes.filter((_, idx) => idx !== i));
    
    // Main submit handler for the edit form
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!editingProduct || !editingProduct.product_id) {
            showConfirmation({
                title: 'Error',
                message: 'Product ID is missing. Cannot submit update.',
                type: 'danger',
                confirmText: 'OK',
                onConfirm: closeConfirmation
            });
            return;
        }

        // --- Data Extraction and Combination ---
        
        // 1. Get standard sizes from the form (checkboxes) 
        // NOTE: We rely on the `editingProduct.sizes` state which is updated by handleStandardSizeToggle.
        // The sizes array in state includes all selected standard sizes and the custom sizes that were initially loaded.
        
        // 2. Get and filter manual sizes
        const manualSizesFiltered = manualSizes.map(s => s.trim()).filter(Boolean);
        
        // 3. Combine all sizes and ensure uniqueness
        // Start with the sizes currently checked in state (which includes only standard sizes based on the logic in handleStandardSizeToggle),
        // then combine with the manually entered sizes, ensuring no duplicates.
        const standardSizesChecked = editingProduct.sizes.filter(s => STANDARD_SIZES.includes(s));
        const allSizes = [...new Set([...standardSizesChecked, ...manualSizesFiltered])]; 
        
        // 4. Create the final data payload **MAPPING FRONTEND STATE KEYS TO BACKEND EXPECTED KEYS**
        const updatedData = {
            // Keys expected by the backend PUT /api/products/:id route
            productName: editingProduct.name, // Use the 'name' from state
            description: editingProduct.description,
            categoryName: editingProduct.category, // Use the 'category' from state
            productType: editingProduct.product_type, // Use the 'product_type' from state
            price: editingProduct.price, 
            deliveryPrice: editingProduct.deliveryPrice,
            stockCount: editingProduct.stockCount,
            sizes: allSizes, // Use the combined, unique array
            image: editingProduct.image // Crucial: Pass the existing image data to persist it!
        };

        // --- Validation ---
        const { productName, description, categoryName, productType, price, deliveryPrice, stockCount } = updatedData;
        
        if (!productName || !description || !categoryName || !productType) {
            showConfirmation({
                title: 'Validation Error',
                message: 'Please fill in all required text/select fields.',
                type: 'warning',
                confirmText: 'OK',
                onConfirm: closeConfirmation
            });
            return;
        }

        // Validation for numbers (non-negative integer) - assuming integer is required based on backend
        const validateNumber = (val, maxDigits, fieldName, allowZero = true) => {
            const num = Number(val);
            // Check if it's a number, non-negative, and a whole number (no fractional parts)
            const isValid = !isNaN(num) && num >= 0 && Number.isInteger(num);
            if (!isValid) return `${fieldName} must be a non-negative whole number.`;
            if (!allowZero && num === 0) return `${fieldName} must be a positive whole number.`;
            if (String(num).length > maxDigits) return `${fieldName} cannot have more than ${maxDigits} digits.`;
            return null;
        };

        const priceError = validateNumber(price, 8, 'Price');
        const deliveryPriceError = validateNumber(deliveryPrice, 8, 'Delivery Price');
        // Stock count must be positive integer (min="1" in the input)
        const stockError = validateNumber(stockCount, 3, 'Stock Count', false); 
        
        if (priceError || deliveryPriceError || stockError) {
            const message = [
                priceError, 
                deliveryPriceError, 
                stockError, 
            ]
            .filter(Boolean)
            .join(' ');

            showConfirmation({
                title: 'Validation Error',
                message: message || 'Please check the number fields for errors.',
                type: 'warning',
                confirmText: 'OK',
                onConfirm: closeConfirmation
            });
            return;
        }

        if (allSizes.length === 0) {
            showConfirmation({
                title: 'Validation Error',
                message: 'Please select or add at least one size.',
                type: 'warning',
                confirmText: 'OK',
                onConfirm: closeConfirmation
            });
            return;
        }

        // --- Submission ---
        handleUpdate(editingProduct.product_id, updatedData); 
    };

    const setProductForEditing = (product) => {
        // Deep copy the product data and set initial state for editing
        setEditingProduct({
            ...product,
            // Ensure numbers are numbers, not strings from the initial fetch
            price: Number(product.price),
            deliveryPrice: Number(product.deliveryPrice),
            stockCount: Number(product.stockCount),
        });
    }

    const formatPrice = (price) => {
        return `Rs. ${Number(price).toLocaleString()}`;
    };

    if (loading) return <div className="loading">Loading products...</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="listitem_container">
            <h2>Product List</h2>
            {/* ... Product Grid Rendering (Remains unchanged) ... */}
            <div className="product-grid">
                {products.map(product => (
                    <div key={product.product_id} className="product-card">
                        <div className="product-image">
                            {product.image ? (
                                <img 
                                    src={`data:image/jpeg;base64,${product.image}`} 
                                    alt={product.name}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/placeholder-image.jpg'; 
                                    }}
                                />
                            ) : (
                                <div className="no-image">
                                    <i className="fas fa-image"></i>
                                </div>
                            )}
                        </div>
                        <div className="product-details">
                            <h3>{product.name}</h3>
                            <p className="category">{product.category}</p>
                            <p className="price">{formatPrice(product.price)}</p>
                            <div className="sizes">
                                {(product.sizes || []).map(size => (
                                    <span key={size} className="size-tag">{size}</span>
                                ))}
                            </div>
                            <p className="stock">Stock: {product.stockCount}</p>
                            <div className="product-actions">
                                <button 
                                    className="edit-btn"
                                    onClick={() => setProductForEditing(product)}
                                >
                                    Edit
                                </button>
                                <button 
                                    className="delete-btn"
                                    onClick={() => handleDelete(product.product_id)} 
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Edit Modal (Now uses controlled components) */}
            {editingProduct && (
                <div className="edit-modal">
                    <div className="modal-content">
                        <h3>Edit Product: {editingProduct.name}</h3>
                        <form onSubmit={handleSubmit}>
                            {/* Product Name */}
                            <div className="form-group">
                                <label>Product Name</label>
                                <input
                                    type="text"
                                    name="productName"
                                    value={editingProduct.name} 
                                    onChange={handleEditChange} 
                                    maxLength={40}
                                    required
                                />
                            </div>

                            {/* Description */}
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    name="description"
                                    value={editingProduct.description} 
                                    onChange={handleEditChange} 
                                    maxLength={400}
                                    required
                                />
                            </div>

                            {/* Category */}
                            <div className="form-group">
                                <label>Category</label>
                                <select
                                    name="categoryName"
                                    value={editingProduct.category} 
                                    onChange={handleEditChange} 
                                    required
                                >
                                    {categories.map(category => (
                                        <option key={category.category_id} value={category.category_name}>
                                            {category.category_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Type */}
                            <div className="form-group">
                                <label>Product Type</label>
                                <select
                                    name="productType"
                                    value={editingProduct.product_type} 
                                    onChange={handleEditChange} 
                                    required
                                >
                                    <option value="men">Men</option>
                                    <option value="female">Female</option>
                                    <option value="kid">Kids</option>
                                </select>
                            </div>

                            <div className="form-row">
                                {/* Price */}
                                <div className="form-group">
                                    <label>Price (LKR)</label>
                                    <input
                                        type="number"
                                        name="price"
                                        value={editingProduct.price} 
                                        onChange={handleEditChange} 
                                        min="0"
                                        maxLength={8}
                                        required
                                    />
                                </div>

                                {/* Delivery Price */}
                                <div className="form-group">
                                    <label>Delivery Price (LKR)</label>
                                    <input
                                        type="number"
                                        name="deliveryPrice"
                                        value={editingProduct.deliveryPrice} 
                                        onChange={handleEditChange} 
                                        min="0"
                                        maxLength={8}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Stock Count */}
                            <div className="form-group">
                                <label>Stock Count</label>
                                <input
                                    type="number"
                                    name="stockCount"
                                    value={editingProduct.stockCount} 
                                    onChange={handleEditChange} 
                                    min="1"
                                    maxLength={3}
                                    required
                                />
                            </div>

                            {/* Standard Sizes Checkboxes */}
                            <div className="form-group">
                                <label>Standard Sizes</label>
                                <div className="size-checkboxes">
                                    {STANDARD_SIZES.map(size => (
                                        <label key={size} className="size-label">
                                            <input
                                                type="checkbox"
                                                name="sizes"
                                                value={size}
                                                // Now uses checked property and a dedicated handler for controlled state
                                                checked={editingProduct.sizes.includes(size)} 
                                                onChange={handleStandardSizeToggle}
                                            />
                                            {size}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Manual Sizes Inputs */}
                            <div className="form-group">
                                <label>Custom Sizes</label>
                                {manualSizes.map((size, i) => (
                                    <div key={i} className="manual-size-input">
                                        <input 
                                            type="text" 
                                            value={size} 
                                            maxLength={10} 
                                            onChange={e => handleManualSizeChange(i, e.target.value)} 
                                            placeholder="Enter custom size"
                                        />
                                        {(manualSizes.length > 1 || (manualSizes.length === 1 && size.trim() !== '')) && (
                                            <button type="button" onClick={() => removeManualSize(i)}>×</button>
                                        )}
                                    </div>
                                ))}
                                {/* Only allow adding a new field if the last one isn't empty */}
                                {(manualSizes.length === 0 || manualSizes[manualSizes.length - 1].trim() !== '') && (
                                    <button type="button" onClick={addManualSize}>+ Add Custom Size</button>
                                )}
                            </div>

                            <div className="modal-actions">
                                <button type="submit" className="save-btn">Save Changes</button>
                                <button 
                                    type="button" 
                                    className="cancel-btn"
                                    onClick={() => setEditingProduct(null)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={closeConfirmation}
                title={confirmModal.title}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                confirmText={confirmModal.confirmText}
                type={confirmModal.type}
            />
        </div>
    );
}

export default Listitem;
